import {
  createApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import {
  getAccessToken,
  refreshAccessToken
} from '@/app/common/utils/customFetchBase';
import { getBaseApiPath } from '@/app/common/api/config';

export type RenderSeoReportPdfRequest = {
  analyze_markdown: string;
  title?: string;
};

const MCP_API_BASE = getBaseApiPath();

/**
 * Same auth as mcpApi (Bearer only — no `Path` header).
 * `Path` is not in MCP CORS allow-list and would fail preflight.
 */
const mcpPdfBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, _api, _extraOptions) => {
  const run = async (
    retried: boolean
  ): Promise<{ data: Blob } | { error: FetchBaseQueryError }> => {
    const fetchArgs: FetchArgs =
      typeof args === 'string' ? { url: args } : { ...args };
    const headers = new Headers(fetchArgs.headers as HeadersInit | undefined);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const url = `${MCP_API_BASE}${fetchArgs.url}`;
    const response = await fetch(url, {
      method: fetchArgs.method ?? 'POST',
      headers,
      body:
        fetchArgs.body === undefined || fetchArgs.body === null
          ? undefined
          : typeof fetchArgs.body === 'string'
            ? fetchArgs.body
            : JSON.stringify(fetchArgs.body)
    });

    if (response.status === 401 && !retried) {
      const refreshed = await refreshAccessToken({ failedApiUrl: url });
      if (refreshed) {
        return run(true);
      }
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let msg = text || `HTTP ${response.status}`;
      try {
        const parsed = JSON.parse(text) as { msg?: string };
        if (parsed.msg) msg = parsed.msg;
      } catch {
        /* keep text */
      }
      return {
        error: {
          status: response.status,
          data: msg
        }
      };
    }

    const blob = await response.blob();
    return { data: blob };
  };

  return run(false);
};

/**
 * Panel PDF export via MCP proxy:
 * POST /mcp/v1/seo-report/render → ai_binesh seo-report/render (binary PDF).
 */
const mcpPdfApi = createApi({
  reducerPath: 'mcpPdfApi',
  baseQuery: mcpPdfBaseQuery,
  endpoints: build => ({
    renderSeoReportPdf: build.mutation<Blob, RenderSeoReportPdfRequest>({
      query: ({ analyze_markdown, title }) => ({
        url: '/mcp/v1/seo-report/render',
        method: 'POST',
        body: {
          analyze_markdown,
          ...(title?.trim() ? { title: title.trim() } : {})
        }
      })
    })
  })
});

export const { useRenderSeoReportPdfMutation } = mcpPdfApi;
export default mcpPdfApi;

function resolveMcpFileUrl(downloadPath: string): string {
  if (/^https?:\/\//i.test(downloadPath)) return downloadPath;
  const path = downloadPath.startsWith('/') ? downloadPath : `/${downloadPath}`;
  return `${MCP_API_BASE}${path}`;
}

/** GET a stored chat PDF. The path is `/mcp/v1/seo-report/files/:id`. */
export async function fetchMcpPdfFile(downloadPath: string): Promise<Blob> {
  const run = async (retried: boolean): Promise<Blob> => {
    const url = resolveMcpFileUrl(downloadPath);
    const headers = new Headers();
    const token = getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(url, { method: 'GET', headers });
    if (response.status === 401 && !retried) {
      const refreshed = await refreshAccessToken({ failedApiUrl: url });
      if (refreshed) return run(true);
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.blob();
  };

  return run(false);
}
